
import { elem, styleInstaller } from "./chrome";

export interface AutoBanQuizActions {
  /** The selected 1-based answer, sent only after an explicit submit. */
  readonly onAnswer?: (answer: number) => void;
  /** Ask the server for a fresh question; this does not answer the current one. */
  readonly onRefresh?: () => void;
  /** Injectable clock for deterministic countdowns and mutation tests. */
  readonly now?: () => number;
  /** Localized label for the remaining-time line. */
  readonly remainingLabel?: string;
}

const STYLE_ID = "autoban-quiz-style";
const STYLE = `
.autoban-quiz {
  position: fixed; left: 50%; top: 50%; transform: translate(-50%, -50%);
  z-index: 90; width: min(420px, calc(100vw - 32px)); padding: 16px;
  border: 1px solid rgba(232, 181, 102, .55); border-radius: 8px;
  color: #ede5d7; background: rgba(20, 17, 14, .98);
  box-shadow: 0 16px 52px rgba(0, 0, 0, .72);
  font: 13px/1.45 system-ui, sans-serif; pointer-events: auto;
}
.autoban-quiz-messages { margin-bottom: 12px; color: #f1d7a7; }
.autoban-quiz-message { margin: 2px 0; }
.autoban-quiz-status { margin: 8px 0 12px; color: #bbb1a2; }
.autoban-quiz-answers { display: grid; gap: 6px; }
.autoban-quiz-answer, .autoban-quiz-action {
  padding: 7px 10px; border: 1px solid rgba(222, 187, 125, .35);
  border-radius: 5px; color: inherit; background: rgba(222, 187, 125, .08);
  font: inherit; text-align: left; cursor: pointer;
}
.autoban-quiz-answer[aria-pressed="true"] { border-color: #f0c36a; background: rgba(240, 195, 106, .24); }
.autoban-quiz-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 14px; }
`;

const installStyle = styleInstaller(STYLE_ID, STYLE);

function remainingSeconds(endAt: number, now: number): number {
  return Math.max(0, Math.ceil((endAt - now) / 1000));
}

/** Format the compact countdown without claiming a locale the client does not ship. */
export function formatQuizTime(seconds: number): string {
  const safe = Math.max(0, Math.floor(seconds));
  const minutes = Math.floor(safe / 60);
  const rest = safe % 60;
  return minutes > 0 ? `${minutes}m ${rest}s` : `${rest}s`;
}

export class AutoBanQuizPanel {
  private readonly root: HTMLElement;
  private readonly messages: HTMLElement[];
  private readonly answers: HTMLButtonElement[];
  private readonly status: HTMLElement;
  private answerButton: HTMLButtonElement;
  private answer = 0;
  private endAt = 0;

  constructor(
    container: HTMLElement,
    private readonly actions: AutoBanQuizActions = {},
  ) {
    const doc = container.ownerDocument;
    installStyle(doc);

    this.root = elem(doc, "section", "autoban-quiz");
    this.root.hidden = true;
    this.root.setAttribute("role", "alertdialog");
    this.root.setAttribute("aria-modal", "true");

    const messages = elem(doc, "div", "autoban-quiz-messages");
    this.messages = [0, 1].map(() => {
      const message = elem(doc, "p", "autoban-quiz-message");
      messages.append(message);
      return message;
    });
    this.status = elem(doc, "div", "autoban-quiz-status");

    const answerList = elem(doc, "div", "autoban-quiz-answers");
    this.answers = [0, 1, 2].map((index) => {
      const button = doc.createElement("button");
      button.type = "button";
      button.className = "autoban-quiz-answer";
      button.setAttribute("aria-pressed", "false");
      button.addEventListener("click", () => this.select(index));
      answerList.append(button);
      return button;
    });
    const actionBar = elem(doc, "div", "autoban-quiz-actions");
    this.answerButton = doc.createElement("button");
    this.answerButton.type = "button";
    this.answerButton.className = "autoban-quiz-action";
    // `AUTOBAN_QUIZ_ANSWER`/`AUTOBAN_QUIZ_REFRESH` (`uiscriptlocale.py:3-4`),
    // shouting caps and all -- `locale_interface.txt` carries no override for
    // either key, so the hardcoded default is what the shipped client drew.
    this.answerButton.textContent = "ANSWER";
    this.answerButton.addEventListener("click", () => this.submit());
    const refresh = doc.createElement("button");
    refresh.type = "button";
    refresh.className = "autoban-quiz-action";
    refresh.textContent = "REFRESH";
    refresh.addEventListener("click", () => this.actions.onRefresh?.());
    actionBar.append(refresh, this.answerButton);

    this.root.append(messages, this.status, answerList, actionBar);
    this.root.addEventListener("keydown", (event) => {
      if (event.key !== "Escape") return;
      // The native window returns True here: Escape is consumed, not a way to
      // dismiss a server challenge (`uiautoban.py:128-129`).
      event.preventDefault();
      event.stopPropagation();
    });
    container.append(this.root);
  }

  /** Draw two messages and three answer choices from the server's quiz text. */
  open(quiz: string, durationSeconds: number, now = this.actions.now?.() ?? Date.now()): void {
    const lines = quiz.split("|");
    if (lines.length >= 5) {
      this.messages.forEach((message, index) => { message.textContent = lines[index] ?? ""; });
      this.answers.forEach((button, index) => {
        button.textContent = lines[index + 2] ?? "";
        button.hidden = false;
      });
    }
    this.answer = 0;
    this.answers.forEach((button) => button.setAttribute("aria-pressed", "false"));
    this.endAt = now + Math.max(0, durationSeconds) * 1000;
    this.update(now);
    this.root.hidden = false;
    this.answers[0]?.focus();
  }

  /** Select exactly one 1-based answer; no packet is emitted yet. */
  select(index: number): void {
    if (!this.showing || index < 0 || index >= this.answers.length) return;
    this.answers.forEach((button, buttonIndex) => button.setAttribute("aria-pressed", String(buttonIndex === index)));
    this.answer = index + 1;
  }

  /** Submit only a selected answer, then close the local challenge window. */
  submit(): void {
    if (!this.showing || this.answer === 0) return;
    const answer = this.answer;
    this.close();
    this.actions.onAnswer?.(answer);
  }

  /** Refresh is a server request and leaves the current challenge untouched. */
  refresh(): void {
    this.actions.onRefresh?.();
  }

  /** Repaint the remaining-time line; expiry clamps at zero. */
  update(now = this.actions.now?.() ?? Date.now()): void {
    // `AUTOBAN_QUIZ_REST_TIME` (`uiscriptlocale.py:5`) has no
    // `locale_interface.txt` override either, so the label the shipped client
    // showed was the bare identifier, never a translated phrase.
    const label = this.actions.remainingLabel ?? "REST_TIME";
    this.status.textContent = `${label}: ${formatQuizTime(remainingSeconds(this.endAt, now))}`;
  }

  close(): void {
    this.root.hidden = true;
  }

  get showing(): boolean {
    return !this.root.hidden;
  }

  get element(): HTMLElement {
    return this.root;
  }
}
